import React from 'react'
import { observer } from 'mobx-react-lite'

import Box from '@mui/material/Box'
import Divider from '@mui/material/Divider'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import Stack from '@mui/material/Stack'
import { useTheme } from '@mui/material/styles'
import { alpha } from '@mui/material/styles'

import { DataGridPro, GridColDef } from '@mui/x-data-grid-pro'

import { InstitutionLogo, Text } from '@mxenabled/mxui'

import {
  CurrencyText,
  InformationTooltip,
  useAppConfig,
  useGlobalCopyStore,
  useWidgetContainerProvider,
} from '../../common'
import { DATE_FORMATS_INTL, formatDate } from '../../core/constants'
import { type Account } from '../../core'
import { useCashflowUiStore } from '../context/hooks'
import { recurrenceFilter } from '../util/Filters'

import RemainingCash from './RemainingCash'
import { formatCurrency } from '../../core/utils/NumberFormatting'
import { getAccountStartingBalance } from '../../core/utils/AccountUtils'

const SummaryTab = () => {
  const { availableWidth } = useWidgetContainerProvider()
  const theme = useTheme()
  const { cashflow: customCopy } = useGlobalCopyStore()
  const {
    config: { display_available_balance_in_accounts: useAvailable },
  } = useAppConfig()
  const { dateRange, selectedCashAccounts, upcomingExpenses, upcomingIncome } = useCashflowUiStore()

  const columns: GridColDef[] = [
    {
      field: 'account',
      flex: 1.5,
      headerName: customCopy.accounts,
      renderCell: ({ value: { name, nickname, institution_guid, account_number } }) => {
        const accountName = nickname || name

        return (
          <Stack alignItems="center" flexDirection="row" gap={12}>
            <InstitutionLogo
              alt={customCopy.institution_logo}
              institutionGuid={institution_guid || ''}
              size={16}
            />
            <Text variant="body2">{`${accountName} ${account_number ? `*${account_number}` : ''}`}</Text>
          </Stack>
        )
      },
    },
    {
      field: 'balance',
      flex: 1,
      headerName: customCopy.balance,
      type: 'number',
      renderCell: (params) => (
        <Text color={theme.palette.text.primary} variant="body2">
          {formatCurrency(params.value, '0,0.00')}
        </Text>
      ),
    },
    {
      field: 'income',
      flex: 1,
      headerName: customCopy.projected_income,
      type: 'number',
      renderCell: (params) => (
        <Text color={theme.palette.text.primary} variant="body2">
          {`+${formatCurrency(params.value, '0,0.00')}`}
        </Text>
      ),
    },
    {
      field: 'expenses',
      flex: 1.1,
      headerName: customCopy.projected_expenses,
      type: 'number',
      renderCell: (params) => (
        <Text color={theme.palette.text.primary} variant="body2">
          {`-${formatCurrency(params.value, '0,0.00')}`}
        </Text>
      ),
    },
    {
      field: 'remaining',
      flex: 1,
      headerName: customCopy.remaining,
      type: 'number',
      renderCell: (params) => {
        const total = params.row.balance + params.row.income - params.row.expenses

        return (
          <Text
            color={Math.sign(total) === -1 ? theme.palette.error.main : theme.palette.text.primary}
            variant="body2"
          >
            {formatCurrency(total, '0,0.00')}
          </Text>
        )
      },
    },
  ]

  const rows = React.useMemo(() => {
    return selectedCashAccounts.map((account: Account) => {
      const totalprojectedExpenses = upcomingExpenses
        .filter(recurrenceFilter(account, dateRange))
        .reduce((acc, curr) => acc + curr.amount, 0)

      const totalProjectedIncome = upcomingIncome
        .filter(recurrenceFilter(account, dateRange))
        .reduce((acc, curr) => acc + curr.amount, 0)

      const startingBalance = getAccountStartingBalance(account, useAvailable)
      const remaining = startingBalance + totalProjectedIncome - totalprojectedExpenses

      return {
        account,
        balance: startingBalance,
        id: account.guid,
        income: totalProjectedIncome,
        expenses: totalprojectedExpenses,
        remaining,
      }
    })
  }, [selectedCashAccounts, upcomingExpenses, upcomingIncome, useAvailable, dateRange])

  return (
    <Box
      sx={{
        width: '100%',
      }}
    >
      <Stack
        direction={availableWidth < 540 ? 'column' : 'row'}
        justifyContent="space-between"
        mb={12}
      >
        <Stack alignItems="center" direction="row" gap={8}>
          <Text
            bold={true}
            sx={{
              fontSize: 15,
            }}
          >
            {customCopy.cash_inflow_and_outflow}
          </Text>
          <InformationTooltip
            body={customCopy.table_tooltip_body}
            title={customCopy.table_tooltip_title}
          />
        </Stack>
        <Text bold={true} sx={{ fontSize: 15 }}>
          {formatDate(dateRange.start, DATE_FORMATS_INTL.FULL_MONTH_DAY)} -{' '}
          {formatDate(dateRange.end, DATE_FORMATS_INTL.FULL_MONTH_DAY)}
        </Text>
      </Stack>
      {availableWidth > 650 ? (
        <DataGridPro
          columnHeaderHeight={48}
          columns={columns}
          density="standard"
          disableColumnMenu={true}
          disableRowSelectionOnClick={true}
          hideFooter={true}
          rowHeight={48}
          rows={rows}
          sx={{
            border: '1px solid',
            borderColor: 'neutral.light',
            borderRadius: 1,
            '& .MuiDataGrid-columnHeader .MuiIconButton-root': {
              color: theme.palette.primary.main,
              '&:hover': {
                backgroundColor: alpha(theme.palette.primary.main, 0.1),
              },
            },
            '& .MuiDataGrid-columnHeaderTitle': {
              fontFamily: theme.typography.h3.fontFamily,
              fontSize: theme.typography.body2.fontSize,
            },
            '& .MuiDataGrid-sortIcon': {
              color: theme.palette.primary.main,
            },
          }}
        />
      ) : (
        rows.map((row) => {
          const { name, nickname, institution_guid, account_number } = row.account
          const accountName = nickname || name

          return (
            <List
              key={row.id}
              sx={{
                border: '1px solid',
                borderColor: 'neutral.light',
                borderRadius: 1,
                marginBottom: 24,
              }}
            >
              <ListItem sx={{ minHeight: 'auto' }}>
                <Stack width="100%">
                  <Stack
                    alignItems="center"
                    flexDirection="row"
                    justifyContent="space-between"
                    px={16}
                    py={12}
                  >
                    <Stack alignItems="center" flexDirection="row" gap={8} width="50%">
                      <InstitutionLogo
                        alt={customCopy.institution_logo}
                        institutionGuid={institution_guid || ''}
                        size={16}
                      />
                      <Text variant="body2">{`${accountName} ${account_number ? `*${account_number}` : ''}`}</Text>
                    </Stack>
                    <CurrencyText amount={row.balance} bold={true} variant="body2" />
                  </Stack>
                  <Divider sx={{ width: '100%' }} />
                  <Stack
                    alignItems="center"
                    flexDirection="row"
                    justifyContent="space-between"
                    px={16}
                    py={12}
                  >
                    <Text variant="body2">{customCopy.projected_income}</Text>
                    <CurrencyText amount={row.income} bold={true} symbol="+" variant="body2" />
                  </Stack>
                  <Divider sx={{ width: '100%' }} />
                  <Stack
                    alignItems="center"
                    flexDirection="row"
                    justifyContent="space-between"
                    px={16}
                    py={12}
                  >
                    <Text variant="body2">{customCopy.projected_expenses}</Text>
                    <CurrencyText amount={row.expenses} bold={true} symbol="-" variant="body2" />
                  </Stack>
                  <Divider sx={{ width: '100%' }} />
                  <Stack
                    alignItems="center"
                    bgcolor="background.default"
                    flexDirection="row"
                    gap={8}
                    justifyContent="space-between"
                    px={16}
                    py={12}
                    sx={{ borderBottomLeftRadius: '4px', borderBottomRightRadius: '4px' }}
                  >
                    <RemainingCash remaining={row.remaining} />
                  </Stack>
                </Stack>
              </ListItem>
            </List>
          )
        })
      )}
    </Box>
  )
}

export default observer(SummaryTab)
